import { Button } from "@/lib/components/Button/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/lib/components/Form";
import { Input } from "@/lib/components/Input";
import { Label } from "@/lib/components/Label";
import { RadioGroup, RadioGroupItem } from "@/lib/components/RadioGroup";
import Toast from "@/lib/components/Toast";
import { useIsFeatureEnabled } from "@/lib/feature-flags";
import { useInvalidateBeatSyncsQuery } from "@/lib/queries/beat-sync-queries";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import { BackgroundMusicApi } from "@/lib/api/background-music-api";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { BeatSyncDto } from "@repo/api/background-music";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { IconTrash } from "@tabler/icons-react";
import CuttingPointsView from "./CuttingPointsView";
import {
  CUTTING_POINT_GAP_ERROR_MESSAGE,
  DEFAULT_BPM,
  validateCuttingPointGaps,
} from "./cutting-points-utils";
import { z } from "zod";
import { BEAT_SYNC_ERROR_MESSAGES, BeatSyncError } from "./beat-sync-clips";

const FormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string(),
});

type FormValues = z.infer<typeof FormSchema>;

type BeatSyncView = "INTRO" | "CUTTING_POINTS" | "FORM";

type SaveMode = "OVERWRITE" | "NEW";

type BeatSyncDialogProps = {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  beatSyncs: BeatSyncDto[];
  trackExternalId: string;
};

export default function BeatSyncDialog(props: BeatSyncDialogProps) {
  const [instanceKey, setInstanceKey] = useState(0);
  const wasOpen = useRef(false);

  useLayoutEffect(() => {
    if (props.open && !wasOpen.current) {
      setInstanceKey((key) => key + 1);
    }
    wasOpen.current = props.open;
  }, [props.open]);

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <BeatSyncDialogBody
        key={instanceKey}
        onOpenChange={props.onOpenChange}
        trackExternalId={props.trackExternalId}
        beatSyncs={props.beatSyncs}
      />
    </Dialog>
  );
}

function BeatSyncDialogBody({
  onOpenChange,
  trackExternalId,
  beatSyncs,
}: Omit<BeatSyncDialogProps, "open">) {
  const syncClipDurationsToBeatSync = useEditorStore(
    (state) => state.service.syncClipDurationsToBeatSync,
  );
  const getPlayerRef = useEditorStore((state) => state.service.getPlayerRef);
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const canManageBeatSync = useIsFeatureEnabled();
  const invalidateBeatSyncs = useInvalidateBeatSyncsQuery();

  const [view, setView] = useState<BeatSyncView>("INTRO");
  const [selectedExternalId, setSelectedExternalId] = useState<
    string | undefined
  >(undefined);
  // The beat sync used to prefill the form and the candidate for overwriting.
  // Undefined means we are creating a beat sync from scratch.
  const [sourceBeatSync, setSourceBeatSync] = useState<BeatSyncDto | undefined>(
    undefined,
  );
  const [saveMode, setSaveMode] = useState<SaveMode>("NEW");
  const [cuttingPoints, setCuttingPoints] = useState<number[]>([]);
  const [bpm, setBpm] = useState(DEFAULT_BPM);
  const [gridOffsetMs, setGridOffsetMs] = useState(0);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const track = useEditorStore(
    (state) => state.backgroundMusics[trackExternalId],
  );

  const selectedBeatSync = beatSyncs.find(
    (beatSync) => beatSync.externalId === selectedExternalId,
  );

  // Keep a valid selection whenever the available beat syncs change.
  useEffect(() => {
    const firstBeatSync = beatSyncs[0];

    if (!firstBeatSync) {
      setSelectedExternalId(undefined);
      return;
    }

    setSelectedExternalId((prev) =>
      prev && beatSyncs.some((beatSync) => beatSync.externalId === prev)
        ? prev
        : firstBeatSync.externalId,
    );
  }, [beatSyncs]);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      title: "",
      description: "",
    },
  });

  // Start the creation flow, optionally prefilling from an existing beat
  // sync which then becomes the candidate for overwriting.
  function startBeatSyncFlow(source: BeatSyncDto | undefined) {
    // Pause the preview so its audio doesn't play over the cutting points editor.
    const player = getPlayerRef()?.current;
    if (player?.isPlaying()) {
      player.pause();
    }

    setSourceBeatSync(source);
    setSaveMode(source ? "OVERWRITE" : "NEW");
    setCuttingPoints([...(source?.cuttingPoints ?? [])].sort((a, b) => a - b));
    setBpm(source?.bpm ?? DEFAULT_BPM);
    setGridOffsetMs(source?.gridOffsetMs ?? 0);
    form.reset({
      title: source?.title ?? "",
      description: source?.description ?? "",
    });
    setView("CUTTING_POINTS");
  }

  function handleAddBeatSync() {
    startBeatSyncFlow(undefined);
  }

  function handleEditBeatSync() {
    const source =
      beatSyncs.length === 1
        ? beatSyncs[0]
        : beatSyncs.find(
            (beatSync) => beatSync.externalId === selectedExternalId,
          );

    startBeatSyncFlow(source);
  }

  async function handleDeleteBeatSync() {
    if (!selectedExternalId) {
      return;
    }

    setIsDeleting(true);

    try {
      await BackgroundMusicApi.deleteBeatSync(
        teamExternalId,
        trackExternalId,
        selectedExternalId,
      );
      Toast.info("Beat sync deleted successfully");
      await invalidateBeatSyncs(teamExternalId, trackExternalId);
    } catch (error) {
      console.error("Failed to delete beat sync:", error);
      Toast.error("Failed to delete beat sync");
    } finally {
      setIsDeleting(false);
      setIsDeleteConfirmOpen(false);
    }
  }

  function handleSyncToBeat() {
    if (!selectedBeatSync) {
      return;
    }

    try {
      syncClipDurationsToBeatSync(selectedBeatSync.cuttingPoints);
      void AnalyticsApi.logBeatSyncTriggered(teamExternalId);
      Toast.info("Video synced to beat");
      onOpenChange(false);
    } catch (error) {
      const errorMessage =
        error instanceof BeatSyncError
          ? BEAT_SYNC_ERROR_MESSAGES[error.type]
          : BEAT_SYNC_ERROR_MESSAGES.FAILED;

      Toast.error(errorMessage);

      if (!(error instanceof BeatSyncError)) {
        console.error(error);
      }
    }
  }

  async function onSubmit(data: FormValues) {
    const normalizedTitle = data.title.trim().toLowerCase();
    const isOverwrite = sourceBeatSync && saveMode === "OVERWRITE";
    const isTitleTaken = beatSyncs.some(
      (beatSync) =>
        beatSync.title.trim().toLowerCase() === normalizedTitle &&
        !(isOverwrite && beatSync.externalId === sourceBeatSync?.externalId),
    );

    if (isTitleTaken) {
      form.setError("title", {
        type: "manual",
        message: "Title is already used",
      });
      return;
    }

    const sortedCuttingPoints = [...cuttingPoints].sort((a, b) => a - b);

    if (!validateCuttingPointGaps(sortedCuttingPoints)) {
      Toast.error(CUTTING_POINT_GAP_ERROR_MESSAGE);
      return;
    }

    try {
      const values = {
        title: data.title,
        description:
          data.description.trim() === "" ? null : data.description.trim(),
        cuttingPoints: sortedCuttingPoints,
        bpm,
        gridOffsetMs,
        isGlobal: true,
      };

      if (sourceBeatSync && saveMode === "OVERWRITE") {
        await BackgroundMusicApi.updateBeatSync(
          teamExternalId,
          trackExternalId,
          sourceBeatSync.externalId,
          values,
        );
      } else {
        await BackgroundMusicApi.createBeatSync(
          teamExternalId,
          trackExternalId,
          values,
        );
      }

      Toast.info("Beat sync saved successfully");
      await invalidateBeatSyncs(teamExternalId, trackExternalId);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save beat sync:", error);
      Toast.error("Failed to save beat sync");
    }
  }

  return (
    <>
      <DialogContent
        className={view === "CUTTING_POINTS" ? "max-w-7xl" : "max-w-lg"}
      >
        {view === "INTRO" && (
          <IntroView
            beatSyncs={beatSyncs}
            selectedExternalId={selectedExternalId}
            onSelectBeatSync={setSelectedExternalId}
            canManageBeatSync={canManageBeatSync}
            onAddBeatSync={handleAddBeatSync}
            onEditBeatSync={handleEditBeatSync}
            onDeleteBeatSync={() => setIsDeleteConfirmOpen(true)}
            onSyncToBeat={handleSyncToBeat}
          />
        )}

        {view === "CUTTING_POINTS" && (
          <CuttingPointsView
            trackSrc={track?.src}
            trackName={track?.name}
            cuttingPoints={cuttingPoints}
            onChangeCuttingPoints={setCuttingPoints}
            bpm={bpm}
            onChangeBpm={setBpm}
            gridOffsetMs={gridOffsetMs}
            onChangeGridOffsetMs={setGridOffsetMs}
            onBack={() => setView("INTRO")}
            onNext={() => setView("FORM")}
          />
        )}

        {view === "FORM" && (
          <BeatSyncFormView
            form={form}
            sourceBeatSync={sourceBeatSync}
            saveMode={saveMode}
            onSaveModeChange={setSaveMode}
            onSubmit={onSubmit}
            onBackToCuttingPoints={() => setView("CUTTING_POINTS")}
          />
        )}
      </DialogContent>

      <Dialog
        open={isDeleteConfirmOpen}
        onOpenChange={(isOpen) => {
          if (!isDeleting) {
            setIsDeleteConfirmOpen(isOpen);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete beat sync</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete
              {selectedBeatSync
                ? ` “${selectedBeatSync.title}”`
                : " this beat sync"}
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isDeleting}
              onClick={() => setIsDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              isLoading={isDeleting}
              onClick={handleDeleteBeatSync}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function IntroView({
  beatSyncs,
  selectedExternalId,
  onSelectBeatSync,
  canManageBeatSync,
  onAddBeatSync,
  onEditBeatSync,
  onDeleteBeatSync,
  onSyncToBeat,
}: {
  beatSyncs: BeatSyncDto[];
  selectedExternalId: string | undefined;
  onSelectBeatSync: (externalId: string) => void;
  canManageBeatSync: boolean;
  onAddBeatSync: () => void;
  onEditBeatSync: () => void;
  onDeleteBeatSync: () => void;
  onSyncToBeat: () => void;
}) {
  const showBeatSyncOptions = canManageBeatSync
    ? beatSyncs.length > 0
    : beatSyncs.length > 1;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Sync video to beat</DialogTitle>
        <DialogDescription>
          Automatically cut your clips so they change in time with the beat of
          your background music. This will automatically adjust the timing of
          your clips.
        </DialogDescription>
      </DialogHeader>
      {showBeatSyncOptions && (
        <RadioGroup
          value={selectedExternalId}
          onValueChange={onSelectBeatSync}
          className="gap-3"
        >
          {beatSyncs.map((beatSync) => (
            <label
              key={beatSync.externalId}
              htmlFor={beatSync.externalId}
              className="flex cursor-pointer items-center gap-3"
            >
              <RadioGroupItem
                value={beatSync.externalId}
                id={beatSync.externalId}
              />
              <span className="text-sm text-text-secondary">
                <span className="font-semibold text-text-primary capitalize">
                  {beatSync.title}
                </span>
                {beatSync.description ? `: ${beatSync.description}` : ""}
              </span>
            </label>
          ))}
        </RadioGroup>
      )}
      <DialogFooter>
        {canManageBeatSync && (
          <>
            {beatSyncs.length === 0 && (
              <Button type="button" variant="outline" onClick={onAddBeatSync}>
                Add beat sync
              </Button>
            )}
            {beatSyncs.length > 0 && (
              <Button
                type="button"
                variant="outline"
                disabled={!selectedExternalId}
                onClick={onEditBeatSync}
              >
                Edit beat sync
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={!selectedExternalId}
              onClick={onDeleteBeatSync}
              aria-label="Delete beat sync"
            >
              <IconTrash />
            </Button>
          </>
        )}
        <Button
          type="button"
          disabled={!selectedExternalId}
          onClick={onSyncToBeat}
        >
          Sync video to beat
        </Button>
        {!canManageBeatSync && (
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        )}
      </DialogFooter>
    </>
  );
}

function BeatSyncFormView({
  form,
  sourceBeatSync,
  saveMode,
  onSaveModeChange,
  onSubmit,
  onBackToCuttingPoints,
}: {
  form: UseFormReturn<FormValues>;
  sourceBeatSync: BeatSyncDto | undefined;
  saveMode: SaveMode;
  onSaveModeChange: (mode: SaveMode) => void;
  onSubmit: (data: FormValues) => void | Promise<void>;
  onBackToCuttingPoints: () => void;
}) {
  const { isSubmitting } = form.formState;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Beat sync details</DialogTitle>
        <DialogDescription>
          Give this beat sync a title and description.
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter a title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <textarea
                    {...field}
                    rows={3}
                    placeholder="Enter a description"
                    className="flex w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-within:border-tertiary-400 focus-within:ring-1 focus-within:ring-tertiary-400 focus:outline-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {sourceBeatSync && (
            <div className="grid gap-2">
              <Label>Save mode</Label>
              <RadioGroup
                value={saveMode}
                onValueChange={(value) => onSaveModeChange(value as SaveMode)}
                className="ml-px flex flex-col gap-3"
              >
                <div className="flex items-center gap-3">
                  <RadioGroupItem
                    value="OVERWRITE"
                    id="beat-sync-save-overwrite"
                  />
                  <Label htmlFor="beat-sync-save-overwrite">
                    Replace current beat sync, “{sourceBeatSync.title}”
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="NEW" id="beat-sync-save-new" />
                  <Label htmlFor="beat-sync-save-new">
                    Save as new beat sync
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={onBackToCuttingPoints}
            >
              Back
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
